import { z } from 'zod';
import { apiEnvelope } from './api-envelope.schema';

// ── Shared fragments ──

export const UserSummarySchema = z.object({
  publicId: z.string().uuid(),
  displayName: z.string(),
  email: z.string().nullable(),
  roles: z.array(z.string()),
  accountStatus: z.string(),
  isEmailVerified: z.boolean(),
  isPhoneVerified: z.boolean(),
});

export const UserSummaryWithPhoneSchema = UserSummarySchema.extend({
  phoneNumber: z.string().nullable(),
});

export const UserRoleSchema = z.object({
  code: z.string(),
  name: z.string(),
  isPrimary: z.boolean(),
});

export const SessionSchema = z.object({
  sessionId: z.string().uuid(),
  deviceName: z.string().nullable(),
  platform: z.string().nullable(),
  ipAddress: z.string().nullable(),
  lastSeenAt: z.string(),
  issuedAt: z.string(),
  expiresAt: z.string(),
  isCurrent: z.boolean(),
});

// ── POST /register ──

export const RegisterResponseDataSchema = z.object({
  publicId: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  roleCode: z.string(),
  accountStatus: z.string(),
  requiresEmailVerification: z.boolean(),
  requiresPhoneVerification: z.boolean(),
});
export const RegisterResponseSchema = apiEnvelope(RegisterResponseDataSchema);

// ── POST /login ──

export const LoginResponseDataSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  tokenType: z.string(),
  user: UserSummarySchema,
});
export const LoginResponseSchema = apiEnvelope(LoginResponseDataSchema);

// ── POST /send-email-otp ──

export const SendEmailOtpResponseDataSchema = z.object({
  otpSent: z.boolean(),
  channel: z.string(),
  maskedDestination: z.string(),
  expiresInSeconds: z.number(),
  cooldownSeconds: z.number(),
  remainingAttemptsThisHour: z.number(),
});
export const SendEmailOtpResponseSchema = apiEnvelope(SendEmailOtpResponseDataSchema);

// ── POST /verify-email-otp ──

export const VerifyEmailOtpResponseDataSchema = z.object({
  emailVerified: z.boolean(),
  isFullyVerified: z.boolean(),
  accountStatus: z.string(),
  nextStep: z.string(),
  message: z.string(),
});
export const VerifyEmailOtpResponseSchema = apiEnvelope(VerifyEmailOtpResponseDataSchema);

// ── POST /send-phone-otp ──

export const SendPhoneOtpResponseDataSchema = z.object({
  otpSent: z.boolean(),
  channel: z.string(),
  maskedDestination: z.string(),
  expiresInSeconds: z.number(),
  cooldownSeconds: z.number(),
  isNewUser: z.boolean(),
});
export const SendPhoneOtpResponseSchema = apiEnvelope(SendPhoneOtpResponseDataSchema);

// ── POST /verify-phone-otp ──

export const VerifyPhoneOtpResponseDataSchema = z.object({
  phoneVerified: z.boolean(),
  isFullyVerified: z.boolean(),
  accountStatus: z.string(),
  nextStep: z.string(),
  message: z.string(),
  accessToken: z.string().nullable(),
  refreshToken: z.string().nullable(),
  expiresIn: z.number().nullable(),
  tokenType: z.string().nullable(),
  isNewUser: z.boolean(),
  requiresEmailVerification: z.boolean(),
  user: UserSummaryWithPhoneSchema.nullable(),
});
export const VerifyPhoneOtpResponseSchema = apiEnvelope(VerifyPhoneOtpResponseDataSchema);

// ── POST /refresh ──

export const RefreshResponseDataSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  tokenType: z.string(),
});
export const RefreshResponseSchema = apiEnvelope(RefreshResponseDataSchema);

// ── POST /logout ──

export const LogoutResponseDataSchema = z.object({
  message: z.string(),
});
export const LogoutResponseSchema = apiEnvelope(LogoutResponseDataSchema);

// ── POST /logout-all ──

export const LogoutAllResponseDataSchema = z.object({
  sessionsRevoked: z.number(),
  message: z.string(),
});
export const LogoutAllResponseSchema = apiEnvelope(LogoutAllResponseDataSchema);

// ── GET /me ──

export const MeResponseDataSchema = z.object({
  publicId: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  profileImageUrl: z.string().nullable(),
  isEmailVerified: z.boolean(),
  isPhoneVerified: z.boolean(),
  accountStatus: z.string(),
  mustChangePassword: z.boolean(),
  roles: z.array(UserRoleSchema),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable(),
});
export const MeResponseSchema = apiEnvelope(MeResponseDataSchema);

// ── GET /sessions ──

export const SessionsResponseDataSchema = z.object({
  currentSessionId: z.string().uuid(),
  sessions: z.array(SessionSchema),
  maxSessions: z.number(),
  activeSessions: z.number(),
});
export const SessionsResponseSchema = apiEnvelope(SessionsResponseDataSchema);

// ── DELETE /sessions/{sessionId} ──

export const RevokeSessionResponseDataSchema = z.object({
  sessionRevoked: z.boolean(),
  activeSessions: z.number(),
  message: z.string(),
});
export const RevokeSessionResponseSchema = apiEnvelope(RevokeSessionResponseDataSchema);

// ── Inferred types ──

export type UserSummary = z.infer<typeof UserSummarySchema>;
export type UserSummaryWithPhone = z.infer<typeof UserSummaryWithPhoneSchema>;
export type UserRole = z.infer<typeof UserRoleSchema>;
export type SessionData = z.infer<typeof SessionSchema>;

export type RegisterResponseData = z.infer<typeof RegisterResponseDataSchema>;
export type LoginResponseData = z.infer<typeof LoginResponseDataSchema>;
export type SendEmailOtpResponseData = z.infer<typeof SendEmailOtpResponseDataSchema>;
export type VerifyEmailOtpResponseData = z.infer<typeof VerifyEmailOtpResponseDataSchema>;
export type SendPhoneOtpResponseData = z.infer<typeof SendPhoneOtpResponseDataSchema>;
export type VerifyPhoneOtpResponseData = z.infer<typeof VerifyPhoneOtpResponseDataSchema>;
export type RefreshResponseData = z.infer<typeof RefreshResponseDataSchema>;
export type LogoutResponseData = z.infer<typeof LogoutResponseDataSchema>;
export type LogoutAllResponseData = z.infer<typeof LogoutAllResponseDataSchema>;
export type MeResponseData = z.infer<typeof MeResponseDataSchema>;
export type SessionsResponseData = z.infer<typeof SessionsResponseDataSchema>;
export type RevokeSessionResponseData = z.infer<typeof RevokeSessionResponseDataSchema>;
